import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigationType } from 'react-router-dom';
import { CartographySelectorModal, getSourceGroupsFiltered, POIBrandsContent } from 'sonata-platform-components';
import { ButtonUI, Checkbox, ModalUI, showNotification } from 'sonata-react-components';

import { AttributionRadiusPrompt } from '@/components/common/MultiFlightMap/components/AttributionPOI/AttributionRadiusPrompt/AttributionRadiusPrompt';
import { useMultiFlightMap } from '@/components/common/MultiFlightMap/MultiFlightMap.context';
import {
  findMatchingTemporaryVenue,
  restoreVenueAssociation
} from '@/components/common/MultiFlightMap/MultiFlightMap.helpers';
import { useModals } from '@/router';
import { postAddVenues } from '@/services/venue.service';

import type { AdminBoundariesChangeItem, AdminBoundariesItemInput } from 'sonata-platform-components';
import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';

import './poiFinderVenuesModal.scss';

type POIBrandItem = {
  address: string;
  coordinates: { latitude: number; longitude: number };
  id: string;
  name: string;
};

type State = {
  source: string | null;
  country: string | null;
  brands: string[];
  categories: string[];
  isLoading: boolean;
  isAddAsAttributionEnabled: boolean;
  showRadiusPrompt: boolean;
  showAdminBoundariesModal: boolean;
  cartographyTagFilters: Record<string, string[]>;
};

export default function PoiFinderVenuesModal() {
  const modalRef = useRef<{ openModal: () => void; closeModal: () => void }>(null);
  const { formatMessage } = useIntl();
  const modals = useModals();
  const navigationType = useNavigationType();
  const {
    addLocationCircle,
    countries,
    entityId,
    entityType,
    onAddBatchVenues,
    sources,
    temporaryVenues,
    updateVenue,
    user,
    userRoles,
    venuesSourceData
  } = useMultiFlightMap();

  const [
    {
      brands,
      categories,
      cartographyTagFilters,
      country,
      isAddAsAttributionEnabled,
      isLoading,
      showAdminBoundariesModal,
      showRadiusPrompt,
      source
    },
    setState
  ] = useState<State>({
    brands: [],
    cartographyTagFilters: {},
    categories: [],
    country: null,
    isAddAsAttributionEnabled: false,
    isLoading: false,
    showAdminBoundariesModal: false,
    showRadiusPrompt: false,
    source: null
  });

  useEffect(() => {
    if (navigationType === 'POP') {
      modals.close();
    } else {
      modalRef.current?.openModal();
    }
  }, [modals, navigationType]);

  const handleStateChange = (poiData: {
    dataSourceType: string;
    selectedCountry: string;
    selectedItems: Set<{ brand: string; category: string }>;
  }) => {
    const selectedItemsData = Array.from(poiData.selectedItems).reduce(
      (acc, entry) => ({
        brands: [...acc.brands, entry.brand],
        categories: [...acc.categories, entry.category]
      }),
      { brands: [], categories: [] } as { brands: string[]; categories: string[] }
    );
    setState(prev => ({
      ...prev,
      brands: selectedItemsData.brands,
      categories: selectedItemsData.categories,
      country: poiData.selectedCountry,
      source: poiData.dataSourceType
    }));
  };

  const handleSave = async (radiusInMeters: number) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, showRadiusPrompt: false }));

      const tokenGroup = {};
      const poiBrandsItems: POIBrandItem[] = await getSourceGroupsFiltered(
        source,
        country,
        { brands, categories },
        { cartographyFilter: cartographyTagFilters },
        tokenGroup
      );

      await Promise.all(
        poiBrandsItems
          .filter(item => item.coordinates?.latitude && item.coordinates?.longitude)
          .map(item =>
            addLocationCircle(item.coordinates.latitude, item.coordinates.longitude, radiusInMeters / 1000, item.name)
          )
      );

      if (isAddAsAttributionEnabled) {
        const venues = poiBrandsItems.map(item => ({
          address: item.address,
          country,
          deleted: false,
          geoDataId: item.id,
          geoDataSourceId: source,
          groups: [],
          latlng: {
            lat: item.coordinates.latitude,
            lng: item.coordinates.longitude
          },
          name: item.name,
          sourceType: 'poiBrands',
          type: 'retail',
          user: user._id
        }));
        const venuesToRestore: Partial<Venue>[] = [];
        const venuesToAdd: typeof venues = [];
        venues.forEach(candidate => {
          const match = findMatchingTemporaryVenue(candidate as Partial<Venue>, temporaryVenues);
          if (!match) {
            venuesToAdd.push(candidate);
          } else if (match.deleted) {
            venuesToRestore.push(restoreVenueAssociation(match, entityId, entityType));
          }
          // else: already associated with this entity — skip, it's a duplicate.
        });

        if (venuesToRestore.length > 0) {
          updateVenue(venuesToRestore);
        }
        if (venuesToAdd.length > 0) {
          const persisted = await postAddVenues(venuesToAdd as unknown as Venue[], {});
          await onAddBatchVenues(persisted as Partial<Venue<string>>[], { attribution: true });
        }
      }

      showNotification(
        'success',
        formatMessage({ defaultMessage: 'Areas added successfully', id: 'poiFinderVenues.notification.add.success' })
      );
      modals.close();
    } catch (_) {
      showNotification(
        'error',
        formatMessage({ defaultMessage: "Couldn't add areas, try again", id: 'poiFinderVenues.notification.add.error' })
      );
    } finally {
      setState(prev => ({ ...prev, isLoading: false, showRadiusPrompt: false }));
    }
  };

  const handleCartographySave = (zoneList: AdminBoundariesChangeItem[]) => {
    const tagFilters = zoneList.reduce(
      (acc, item) => ({
        ...acc,
        [item.shape]: item.list.map((e: AdminBoundariesItemInput) => e.name)
      }),
      {} as Record<string, string[]>
    );
    setState(prev => ({ ...prev, cartographyTagFilters: tagFilters, showAdminBoundariesModal: false }));
  };

  const handleCartographyReset = () => {
    setState(prev => ({ ...prev, cartographyTagFilters: {} }));
  };

  const poiBrandSources = sources.allSources.filter(s => s.type === 'poi' && s.source.includes('poibrand'));
  const notBrandsAndCategories = brands.length === 0 && categories.length === 0;

  return (
    <>
      <ModalUI
        closeModalCallback={modals.close}
        description={formatMessage({
          defaultMessage:
            'Convert any Point of Interest into an activation area with a custom radius by selecting items from our inventory',
          id: 'poiFinderVenues.description'
        })}
        ref={modalRef}
        title={formatMessage({ defaultMessage: 'Areas from POIs', id: 'poiFinderVenuesModal.title' })}
        width="1152px"
      >
        <div className="poiFinderVenuesModal">
          <div className="poiFinderVenuesModal__brands">
            <POIBrandsContent
              campaign={{ _id: entityId }}
              countries={countries.map(c => c.toLocaleUpperCase())}
              filterAddedShops={temporaryVenues.filter(v => !v.deleted && v._id).map(v => v._id!)}
              isLoading={isLoading}
              isModalOpened={true}
              onAdminBoundariesClick={() => setState(prev => ({ ...prev, showAdminBoundariesModal: true }))}
              onAdminBoundariesReset={handleCartographyReset}
              onStateChange={handleStateChange}
              origin="venues"
              originFilters={{
                layerIsochroneFilter: {},
                poiFilters: {},
                tagFilters: cartographyTagFilters
              }}
              shouldFeatureAddFromCampaign={false}
              shouldHideSourceTypeSelector
              sources={poiBrandSources}
              theme="light"
              user={user}
              userRoles={userRoles}
              venuesSourceData={venuesSourceData}
              viewport={{}}
            />
          </div>
          <div className="poiFinderVenuesModal__actions">
            <Checkbox
              active={isAddAsAttributionEnabled}
              disabled={notBrandsAndCategories || isLoading}
              id={'poiFinderVenues-addAsAttribution'}
              labelText={formatMessage({
                defaultMessage: 'Add and set as Attribution POIs',
                id: 'poiFinderVenues.checkbox.attributionPOIs'
              })}
              onClickAction={() =>
                setState(prev => ({ ...prev, isAddAsAttributionEnabled: !prev.isAddAsAttributionEnabled }))
              }
            />
            <ButtonUI
              disabled={notBrandsAndCategories || isLoading}
              icon="fa-regular fa-floppy-disk"
              iconPosition="left"
              id={'poiFinderVenues-addAreas'}
              onClick={() => setState(prev => ({ ...prev, showRadiusPrompt: true }))}
              shape="squareRounded"
              text={formatMessage({ defaultMessage: 'Add areas', id: 'poiFinderVenues.button.addAreas' })}
              type="primary"
            />
          </div>
          {showRadiusPrompt && (
            <AttributionRadiusPrompt
              onCancel={() => setState(prev => ({ ...prev, showRadiusPrompt: false }))}
              onConfirm={handleSave}
            />
          )}
        </div>
      </ModalUI>
      {showAdminBoundariesModal && (
        <CartographySelectorModal
          id={'poiFinderVenues-cartographySelectorModal'}
          initialCountry={countries[0]?.toUpperCase()}
          initialItems={Object.entries(cartographyTagFilters).flatMap(([shape, ids]) =>
            ids.map(
              (name): AdminBoundariesItemInput => ({
                country: countries[0]?.toUpperCase() ?? '',
                description: name,
                name,
                shape
              })
            )
          )}
          isModalOpened={showAdminBoundariesModal}
          onClose={() => setState(prev => ({ ...prev, showAdminBoundariesModal: false }))}
          onSave={handleCartographySave}
          oneBoundaryType
          sonataLICountries
          theme="light"
          user={user}
          userRoles={userRoles}
        />
      )}
    </>
  );
}
